import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Heart, MessageCircle, X, Loader2, Send } from "lucide-react";

/**
 * ProfilePostCard — a single post row in the Profile POSTS tab.
 *
 * - Clicking the card opens the original community post (`/post/:id`).
 * - Clicking the image opens a full-screen lightbox (does NOT navigate).
 * - Like + reply happen inline without leaving the profile.
 *
 * Props:
 *   post          the post document ({id, content, image_url, likes_count, replies_count, created_at})
 *   isConnected   whether a wallet is connected (gates like/reply)
 *   api           an authenticated apiService (likePost/unlikePost/createPost)
 *   onOpen        () => navigate to the post detail page
 */
export default function ProfilePostCard({ post, isConnected, api, onOpen }) {
  const [liked, setLiked] = useState(false);
  const [likesCount, setLikesCount] = useState(post.likes_count || 0);
  const [repliesCount, setRepliesCount] = useState(post.replies_count || 0);
  const [busyLike, setBusyLike] = useState(false);
  const [showReply, setShowReply] = useState(false);
  const [replyText, setReplyText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [lightbox, setLightbox] = useState(false);

  const requireAuth = () => {
    if (!isConnected) {
      toast.error("Connect your wallet first");
      return false;
    }
    return true;
  };

  const toggleLike = async (e) => {
    e.stopPropagation();
    if (!requireAuth() || busyLike) return;
    setBusyLike(true);
    try {
      await api.likePost(post.id);
      setLiked(true);
      setLikesCount((c) => c + 1);
    } catch (err) {
      if (err.response?.status === 400) {
        // Already liked → unlike (toggle off)
        try {
          await api.unlikePost(post.id);
          setLiked(false);
          setLikesCount((c) => Math.max(0, c - 1));
        } catch {
          toast.error("Could not update like");
        }
      } else {
        toast.error("Could not like post");
      }
    } finally {
      setBusyLike(false);
    }
  };

  const toggleReply = (e) => {
    e.stopPropagation();
    if (!requireAuth()) return;
    setShowReply((s) => !s);
  };

  const submitReply = async (e) => {
    e.stopPropagation();
    if (!requireAuth() || !replyText.trim() || submitting) return;
    setSubmitting(true);
    try {
      await api.createPost({ content: replyText.trim(), parent_id: post.id });
      setRepliesCount((c) => c + 1);
      setReplyText("");
      setShowReply(false);
      toast.success("Reply posted");
    } catch {
      toast.error("Could not post reply");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Card
        className="glass-card hover:bg-muted/30 transition-colors cursor-pointer"
        onClick={onOpen}
        data-testid={`profile-post-${post.id}`}
      >
        <CardContent className="p-4">
          {post.content && (
            <p className="whitespace-pre-wrap break-words">{post.content}</p>
          )}

          {post.image_url && (
            <img
              src={post.image_url}
              alt="Post attachment"
              loading="lazy"
              onClick={(e) => { e.stopPropagation(); setLightbox(true); }}
              className="mt-3 rounded-xl border border-border/50 w-full max-h-96 object-cover hover:opacity-90 transition-opacity"
              data-testid={`profile-post-image-${post.id}`}
            />
          )}

          {/* Actions */}
          <div className="flex items-center gap-5 mt-3 text-sm text-muted-foreground">
            <button
              onClick={toggleReply}
              className="inline-flex items-center gap-1.5 hover:text-orange-400 transition-colors"
              data-testid={`profile-post-reply-btn-${post.id}`}
            >
              <MessageCircle className="w-4 h-4" />
              <span>{repliesCount}</span>
            </button>
            <button
              onClick={toggleLike}
              disabled={busyLike}
              className={`inline-flex items-center gap-1.5 transition-colors ${liked ? "text-red-500" : "hover:text-red-500"}`}
              data-testid={`profile-post-like-btn-${post.id}`}
            >
              <Heart className={`w-4 h-4 ${liked ? "fill-red-500" : ""}`} />
              <span>{likesCount}</span>
            </button>
            <span className="ml-auto text-xs">{new Date(post.created_at).toLocaleDateString()}</span>
          </div>

          {/* Inline reply composer */}
          {showReply && (
            <div className="mt-3" onClick={(e) => e.stopPropagation()}>
              <Textarea
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                placeholder="Write a reply…"
                rows={2}
                className="text-sm"
                data-testid={`profile-post-reply-input-${post.id}`}
              />
              <div className="flex justify-end gap-2 mt-2">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={(e) => { e.stopPropagation(); setShowReply(false); setReplyText(""); }}
                >
                  Cancel
                </Button>
                <Button
                  size="sm"
                  onClick={submitReply}
                  disabled={!replyText.trim() || submitting}
                  className="bg-orange-600 hover:bg-orange-700"
                  data-testid={`profile-post-reply-submit-${post.id}`}
                >
                  {submitting ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Send className="w-3 h-3 mr-1" /> Reply</>}
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Full-screen image lightbox */}
      {lightbox && post.image_url && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 backdrop-blur-sm p-4"
          onClick={() => setLightbox(false)}
          data-testid={`profile-post-lightbox-${post.id}`}
        >
          <button
            className="absolute top-4 right-4 text-white/80 hover:text-white"
            onClick={(e) => { e.stopPropagation(); setLightbox(false); }}
            aria-label="Close"
          >
            <X className="w-7 h-7" />
          </button>
          <img
            src={post.image_url}
            alt="Post attachment"
            className="max-w-full max-h-full object-contain rounded-lg"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </>
  );
}
